import "./style.scss";

import { createFilterOptions } from "@mui/material";
import Plus from "assets/img/svg/plus";
import AccountingNumbersTableInput from "components/BeneficiaryFormContainer/AccountingNumbersTableInput";
import BPPaymentMethod from "components/shared/BPPanelSettings/BPPaymentMethod";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import DateFormComponent from "components/shared/FormComponent/DateFormComponent";
import SwitchFormComponent from "components/shared/FormComponent/SwitchFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import NewElement from "components/shared/NewElement/NewElement";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { BeneficiaryAuxAccountMap } from "containers/Beneficiars/types";
import { getCountries } from "countries";
import { getSocialSecurityPlaceholder, socialSecurityFieldError } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchTypologies } from "store/accountingStore";
import { fetchCreateBeneficiary } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

export interface IAcceptedPayments {
    acceptedPayments: string;
    ribOwnerName: string;
    iban: string;
    swift: string;
    paypalEmail: string;
    bankCheck: number;
    bankingMetadata: Record<string, string>;
}

export interface ISocialProfileOption {
    type: string;
    description: string;
}

export const socialProfileList: ISocialProfileOption[][] = [
    [
        { type: "1", description: "Artistes-Auteurs Assujettis aux précomptes" },
        { type: "2", description: "Artistes-Auteurs Dispensés de précomptes" },
        { type: "3", description: "Artistes-Auteurs résidents fiscaux étrangers" },
        { type: "4", description: "Artistes-Auteurs Décédés" },
        { type: "5", description: "Diffuseur Facturation des Contributions 1,10%" },
        { type: "6", description: "Diffuseur Facturation du Droit d'utilisation" },
        {
            type: "7",
            description: "Artistes-Auteurs Rémunération du Droit d’exploitation",
        },
        {
            type: "8",
            description: "Artistes-Auteurs Rémunération du Droit de prêt",
        },
        {
            type: "9",
            description: "Artistes-Auteurs Rémunération du droit de reprographie",
        },
        {
            type: "L",
            description: "Artistes-Auteurs Revenus précomptés non soumis à contribution diffuseur",
        },
        {
            type: "AS",
            description: "Associations (dons non déclarés à l'URSSAF)",
        },
    ],
    [
        { type: "5", description: "Diffuseur Facturation des Contributions 1,10%" },
        {
            type: "A",
            description: "THC Artistes Auteurs Assujettis aux précomptes",
        },
        { type: "B", description: "THC Artistes Auteurs Dispensés de Précomptes" },
        {
            type: "AS",
            description: "Associations (dons non déclarés à l'URSSAF)",
        },
    ],
    [
        { type: "C", description: "DCA Artistes Auteurs Dispensés de Précomptes" },
        {
            type: "D",
            description:
                "DCA Artistes Auteurs Décédés, ou Non résidents fiscaux, ou anonyme ou OEuvre tombée dans le domaine public",
        },
        { type: "H", description: "OVV Artistes Auteurs Dispensés de Précomptes" },
        {
            type: "I",
            description:
                "OVV Artistes Auteurs Décédés, ou Non résidents fiscaux, ou anonyme ou OEuvre tombée dans le domaine public",
        },
        {
            type: "AS",
            description: "Associations (dons non déclarés à l'URSSAF)",
        },
    ],
];

interface NewBeneficiaryContainerProps {
    modal?: boolean;
    onClose?: (id: number | undefined) => void;
}

const NewBeneficiaryContainer = ({
    modal = false,
    onClose = (id: number | undefined) => {},
}: NewBeneficiaryContainerProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { typologies } = useAppSelector((state) => state.accounting);
    const { companySettings } = useAppSelector((state) => state.settings);

    const [step, setStep] = useState(0);
    const [filterValue, setFilterValue] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [paymentMethods, setEditingProfile] = useState<IAcceptedPayments>({
        acceptedPayments: "",
        ribOwnerName: "",
        iban: "",
        swift: "",
        paypalEmail: "",
        bankCheck: 0,
        bankingMetadata: {},
    });
    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");

    const { companyInfos, getCompanySettings, countryFeatures } = useCompanySettings();
    const usesTwentyPercentVat = getCompanySettings("uses_twenty_percent_vat");

    const currentAccountingSoftware = getCompanySettings("accounting_software");
    const usesAccountingCodeGroups = currentAccountingSoftware === "pennylane";

    const isBelgium = companyInfos?.countryCode == "fr-BE";
    const precountList = [
        { value: 0, label: t("Beneficiars.PrecountNo") },
        { value: 1, label: t("Beneficiars.PrecountYes") },
    ];
    const vatList = [
        t("NewBeneficiary.VATNo"),
        t("NewBeneficiary.VATYes"),
        t("Beneficiars.VATExemption"),
        t("Beneficiars.VATTwentyPercent"),
    ];

    if (isBelgium) {
        vatList.shift();
    }
    if (!usesTwentyPercentVat) {
        vatList.pop();
    }

    const {
        control,
        handleSubmit,
        trigger,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            birthDate: null,
            streetNumber: "",
            zipCode: "",
            city: "",
            country: { name: "", flag: "" },
            // precount: 0, // 0 no, 1 yes
            precount: precountList[0],
            socialProfile: null,
            vat: vatList[0], // 0 retenue a la source 1 TVA 2 franchisé tva
            socialSecurityNumber: "",
            siretNumber: "",
            accountingTypology: "Par défaut",
            accountingCode: "",
            accountingCodeGroups: JSON.stringify([{ mainAccount: "", auxiliaryAccount: "" }]),
            bypassIbanValidation: false,
        },
        mode: "all",
    });

    const filteredTypologies = useMemo(() => {
        return (
            typologies && typologies.filter((typology) => typology.toLowerCase().includes(filterValue.toLowerCase()))
        );
    }, [typologies, filterValue]);

    useEffect(() => {
        dispatch(fetchTypologies());
    }, []);

    const componentsMetaData = [
        {
            title: t("NewBeneficiary.NewBeneficiary"),
            description: t("NewBeneficiary.NewBeneficiarySubtitle"),
        },
        {
            title: `${t("NewBeneficiary.Address")}  (${t("NewElement.Optional")})`,
            description: t("NewBeneficiary.AddressSubtitle", {
                name: `${watch("firstName")} ${watch("lastName")}`,
            }),
        },
        {
            title: t("NewBeneficiary.VATPayment"),
            description: t("NewBeneficiary.VATPaymentSubtitle"),
        },
        {
            title: t("NewBeneficiary.Declaration"),
            description: t("NewBeneficiary.DeclarationSubtitle"),
        },
    ];

    const renderInput = (params: any) => (
        <StyledTextField {...params} error={!!errors.country} helperText={errors.country?.message ?? ""} />
    );

    const renderOptionText = (option: any) => `${option.name}`.trim();

    const renderCountryOption = (props: any, option: any) => (
        <li {...props} className="country-list">
            {/* <span>{option.flag}</span> */}
            <span></span>
            <span>{option.name}</span>
        </li>
    );

    const handleFilterValue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setFilterValue(e.target.value);
    const renderInputTypology = (params: any) => (
        <StyledTextField
            {...params}
            error={!!errors.accountingTypology}
            helperText={errors.accountingTypology?.message ?? ""}
            onChange={handleFilterValue}
        />
    );

    const renderOptionTextTypology = (option: any) => (typeof option === "string" ? option : option.name);

    const renderTypologiesOption = (props: any, option: any) => {
        return option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li
                {...props}
                key={option.id}
                className={`autocomplete-typologies${option == watch("accountingTypology") ? " selected" : ""}`}
            >
                <span>{option}</span>
            </li>
        );
    };

    const renderSocialProfileOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list">
            <span>{option.type}</span>
            <div className="separator"></div>
            <span>{option.description}</span>
        </li>
    );

    const renderVATOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list">
            <span style={{ fontSize: "14px" }}>{option}</span>
        </li>
    );

    const renderPrecountOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list">
            <span style={{ fontSize: "16px" }}>{option.label}</span>
        </li>
    );

    const filterOptions = createFilterOptions({
        matchFrom: "start",
        stringify: (option: any) => option.name,
    });

    const handleNext = async () => {
        const pass = await trigger();
        if (!pass) return;
        setStep((prev) => prev + 1);
    };

    const handlePrev = (i?: number) => setStep((prev) => (i != undefined ? i : prev - 1));

    const handleChange = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, data: any) => {
        setEditingProfile((prev) => {
            if (ev.target.name === "acceptedPayments") {
                if ((data[ev.target.name] as string).includes(ev.target.value)) {
                    data[ev.target.name] = data[ev.target.name].replace(ev.target.value, "").trim();
                    if (ev.target.value === "paypal") data.paypalEmail = "";
                    else if (ev.target.value === "rib") {
                        data.ribOwnerName = "";
                        data.iban = "";
                        data.swift = "";
                    } else if (ev.target.value === "check") data.bankCheck = 0;
                } else {
                    data["acceptedPayments"] = `${data["acceptedPayments"]} ${ev.target.value}`;
                    if (ev.target.value === "check") data.bankCheck = 1;
                }
            } else data[ev.target.name] = ev.target.value;
            return { ...prev!, ...data };
        });
    };

    const onSubmit = async (formData: any) => {
        setSubmitFormStatus("pending");
        const data: any = { ...paymentMethods };
        data.name = formData.firstName ?? "";
        data.surname = formData.lastName ?? "";
        data.email = formData.email.replace(/\s/g, "") || null;
        // Send UTC midnight of the local calendar day so the backend stores the intended
        // day regardless of the user's timezone (no more off-by-one in Americas).
        data.birthDate = formData.birthDate
            ? new Date(
                  Date.UTC(
                      formData.birthDate.getFullYear(),
                      formData.birthDate.getMonth(),
                      formData.birthDate.getDate(),
                  ),
              )
            : null;
        data.street = formData.streetNumber ?? "";
        data.zipCode = formData.zipCode ?? "";
        data.city = formData.city ?? "";
        data.country = formData.country.name ?? "";
        data.precount = formData.precount.value ?? 0;
        data.socialProfileType = formData.socialProfile?.type ?? "";
        data.vat = vatList.findIndex((item) => item == formData.vat) ?? 0;
        data.socialSecurityNumber = formData.socialSecurityNumber ?? "";
        data.siretNumber = formData.siretNumber ?? "";
        data.accountingTypology = formData.accountingTypology ?? "Par défaut";
        data.accountingCode = formData.accountingCode ?? "";
        data.accountingCodeGroups = (JSON.parse(formData.accountingCodeGroups ?? "[]") as BeneficiaryAuxAccountMap[])
            .map((o) => ({
                mainAccount: o.mainAccount?.trim(),
                auxiliaryAccount: o.auxiliaryAccount?.trim(),
            }))
            .filter((o) => o.mainAccount || o.auxiliaryAccount);
        data.bypassIbanValidation = formData.bypassIbanValidation ?? "";
        dispatch(fetchCreateBeneficiary(data)).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                if (modal) onClose(resp.payload.data.id);
                setSubmitFormStatus("succeeded");
            }
            if (resp.meta.requestStatus === "rejected") {
                setSubmitted(false);
                setSubmitFormStatus("failed");
            }
        });
        setSubmitted(true);
    };

    useEffect(() => {
        if (submitFormStatus === "succeeded" && !modal) {
            navigate("/beneficiars");
            setSubmitFormStatus("idle");
        }
    }, [submitFormStatus]);

    return (
        <div className="new-beneficiary-container">
            <form onSubmit={handleSubmit(onSubmit)}>
                <NewElement
                    componentsMetaData={componentsMetaData}
                    currentStep={step}
                    handlePrev={(e: number) => handlePrev(e)}
                    handleNext={handleNext}
                    hasErrors={Object.keys(errors).length > 0}
                    modal={modal}
                    close={onClose}
                    submitted={submitted}
                >
                    <div className="new-beneficiary-form-components">
                        <TextFormComponent
                            label={t("NewBeneficiary.Name")}
                            control={control}
                            controlName="firstName"
                            error={errors.firstName}
                        />
                        <TextFormComponent
                            label={`${t("NewBeneficiary.Surname")}`}
                            control={control}
                            controlName="lastName"
                            error={errors.lastName}
                            required
                            customRequiredMessage={t("NewBeneficiary.RequiredSurname")}
                        />
                        <TextFormComponent
                            label={`${t("NewBeneficiary.Email")} (${t("NewElement.Optional")})`}
                            control={control}
                            controlName="email"
                        />
                        <DateFormComponent
                            label={`${t("NewBeneficiary.BirthDate")} (${t("NewElement.Optional")})`}
                            control={control}
                            controlName="birthDate"
                            error={errors.birthDate}
                        />
                    </div>
                    <div className="new-beneficiary-form-components">
                        <TextFormComponent
                            label={t("NewBeneficiary.StreetNumber")}
                            control={control}
                            controlName="streetNumber"
                        />
                        <div className="new-beneficiary-form-components-flex thirty">
                            <TextFormComponent
                                label={t("NewBeneficiary.ZIPCode")}
                                control={control}
                                controlName="zipCode"
                            />
                            <TextFormComponent label={t("NewBeneficiary.City")} control={control} controlName="city" />
                        </div>
                        <AutocompleteFormComponent
                            label={t("NewBeneficiary.Country")}
                            options={getCountries().filter((country) => !country.silent)}
                            renderInput={renderInput}
                            renderOptionText={renderOptionText}
                            renderOption={(props: any, option: any) => renderCountryOption(props, option)}
                            filterOptions={filterOptions}
                            control={control}
                            controlName="country"
                            error={errors.country}
                        />
                    </div>
                    <div className="new-beneficiary-form-components">
                        <div className="new-beneficiary-form-components-flex half">
                            {/* <SelectFormComponent label={`${t("NewBeneficiary.SocialProfile")}`} items={socialProfileList} control={control} controlName="socialProfile" error={errors.socialProfile} required /> */}
                            {isBelgium ? (
                                <AutocompleteFormComponent
                                    label={t("NewBeneficiary.Precount")}
                                    options={precountList}
                                    renderOptionText={(option: any) => option.label}
                                    defaultFilterOptions
                                    control={control}
                                    renderOption={renderPrecountOption}
                                    controlName="precount"
                                    required
                                    error={errors.precount}
                                    customRequiredMessage={t("NewBeneficiary.RequiredPrecount")}
                                />
                            ) : (
                                <AutocompleteFormComponent
                                    label={t("NewBeneficiary.SocialProfile")}
                                    options={socialProfileList[(companySettings?.distributorType ?? 0) - 1]}
                                    renderOptionText={(option: any) => `${option.type} | ${option.description}`}
                                    renderOption={renderSocialProfileOption}
                                    defaultFilterOptions
                                    control={control}
                                    controlName="socialProfile"
                                    error={errors.socialProfile}
                                    required
                                    customRequiredMessage={t("NewBeneficiary.RequiredSocialProfile")}
                                />
                            )}
                            <AutocompleteFormComponent
                                label={t("NewBeneficiary.VAT")}
                                options={vatList}
                                renderOptionText={(option: any) => option}
                                renderOption={renderVATOption}
                                defaultFilterOptions
                                control={control}
                                controlName="vat"
                                error={errors.vat}
                                required
                            />
                        </div>
                        <ScrollShadowWrapper className="payment-method-scroll-shadow-wrapper">
                            <BPPaymentMethod
                                data={paymentMethods ?? undefined}
                                handleChange={handleChange}
                                company={companySettings}
                            />
                        </ScrollShadowWrapper>
                        {paymentMethods.acceptedPayments.includes("rib") && (
                            <SwitchFormComponent
                                label={`${t("NewBeneficiary.BypassIBANValidation")} (${t(
                                    "NewBeneficiary.BypassIBANValidationPrecisionsShort",
                                )})`}
                                control={control}
                                controlName="bypassIbanValidation"
                            />
                        )}
                    </div>
                    <div className="new-beneficiary-form-components">
                        <TextFormComponent
                            label={`${t("Beneficiars.SecurityNumber")} (${t("NewBeneficiary.Recommended")})`}
                            control={control}
                            controlName="socialSecurityNumber"
                            placeholder={getSocialSecurityPlaceholder(countryFeatures.validation.socialSecurityFormat)}
                            error={socialSecurityFieldError(
                                countryFeatures.validation.socialSecurityFormat,
                                watch("socialSecurityNumber"),
                                t,
                            )}
                            // customValidate={(value) => calculateNIRControlKey(value) || !!!value || t("NewBeneficiary.SocialSecurityValidity")}
                        />
                        <TextFormComponent
                            label={`${t("Beneficiars.Siret")} (${t("NewElement.Optional")})`}
                            control={control}
                            controlName="siretNumber"
                        />
                        <AutocompleteFormComponent
                            label={t("Beneficiars.AccountingTypology")}
                            options={filteredTypologies || []}
                            renderInput={renderInputTypology}
                            renderOptionText={renderOptionTextTypology}
                            renderOption={(props: any, option: any) => renderTypologiesOption(props, option)}
                            control={control}
                            controlName="accountingTypology"
                            error={errors.accountingTypology}
                            required
                            customRequiredMessage={t("NewBeneficiary.RequiredTypology")}
                            // addElement={t("Accounting.AddEntryType")} // TODO: add this
                        />
                        {usesAccountingCodeGroups ? (
                            <AccountingNumbersTableInput control={control as any} controlName="accountingCodeGroups" />
                        ) : (
                            <TextFormComponent
                                label={t("Beneficiars.AccountingCode")}
                                control={control}
                                controlName="accountingCode"
                            />
                        )}
                    </div>
                </NewElement>
            </form>
        </div>
    );
};

export default NewBeneficiaryContainer;
